import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UploadSimple, DownloadSimple, Warning } from '@phosphor-icons/react';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { v: 'ativo', l: 'Ativos (default)', desc: 'Aparecem na lista principal e contam para todos os KPIs.' },
  { v: 'arquivado', l: 'Arquivados', desc: 'Históricos. Não aparecem na lista principal, dashboard ou em silêncio.' },
  { v: 'inativo', l: 'Inativos', desc: 'Pacientes pausados. Excluídos da maioria dos queries.' },
];

export default function ImportPacientesPage() {
  useRequireAdmin();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [defaultStatus, setDefaultStatus] = useState('ativo');

  const handleImport = async () => {
    if (!file) return;
    if (defaultStatus !== 'ativo' && !window.confirm(`Os pacientes serão importados como "${defaultStatus}" por defeito (a coluna 'status' do CSV pode sobrepor por linha). Continuar?`)) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/api/patients/import?default_status=${defaultStatus}`, formData, { withCredentials: true });
      setResults(data);
      const breakdown = Object.entries(data.by_status || {}).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(', ');
      toast.success(`Importados: ${data.imported}, Atualizados: ${data.updated}${breakdown ? ` (${breakdown})` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na importação');
    } finally { setImporting(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/patients/import/template`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'template_pacientes.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao descarregar template'); }
  };

  return (
    <div className="fade-in" data-testid="import-pacientes-page">
      <div className="mb-6">
        <h2 className="font-heading font-medium text-2xl text-brand-text" data-testid="import-title">Importar Pacientes</h2>
        <p className="text-sm text-brand-muted mt-1">Importa a tua base de dados de pacientes via CSV ou Excel.</p>
      </div>

      <div className="bg-white border border-brand-border rounded-3xl p-6 space-y-6">
        <div>
          <h3 className="font-heading font-medium text-sm text-brand-text mb-2">1. Descarregar template</h3>
          <p className="text-xs text-brand-muted mb-3">O template inclui a coluna <code className="bg-brand-bg px-1 rounded">status</code> opcional (ativo / arquivado / inativo).</p>
          <button onClick={handleDownloadTemplate} className="flex items-center gap-2 text-sm text-brand-green hover:text-brand-green-hover transition-colors" data-testid="download-patient-template">
            <DownloadSimple size={16} /> Descarregar template CSV
          </button>
        </div>

        <div className="border-t border-brand-border pt-6">
          <h3 className="font-heading font-medium text-sm text-brand-text mb-2">2. Estado por defeito</h3>
          <p className="text-xs text-brand-muted mb-3">Aplica-se a linhas sem coluna <code className="bg-brand-bg px-1 rounded">status</code>. Ideal para importar históricos directamente como arquivados.</p>
          <div className="grid sm:grid-cols-3 gap-2">
            {STATUS_OPTIONS.map(opt => (
              <button
                key={opt.v}
                onClick={() => setDefaultStatus(opt.v)}
                className={`text-left rounded-2xl border p-3 transition-all ${defaultStatus === opt.v ? 'border-brand-green bg-brand-mint/10' : 'border-brand-border hover:border-brand-green/30'}`}
                data-testid={`default-status-${opt.v}`}
              >
                <p className={`text-sm font-medium ${defaultStatus === opt.v ? 'text-brand-green' : 'text-brand-text'}`}>{opt.l}</p>
                <p className="text-[10px] text-brand-muted mt-1 leading-tight">{opt.desc}</p>
              </button>
            ))}
          </div>
          {defaultStatus !== 'ativo' && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-2xl p-3 flex items-start gap-2" data-testid="non-active-warning">
              <Warning size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-amber-700">Os pacientes vão ser importados como <strong>{defaultStatus}</strong> e não aparecerão na lista principal nem nos KPIs ativos. Podes reativar individualmente depois.</p>
            </div>
          )}
        </div>

        <div className="border-t border-brand-border pt-6">
          <h3 className="font-heading font-medium text-sm text-brand-text mb-2">3. Selecionar ficheiro</h3>
          <label className="border-2 border-dashed border-brand-mint rounded-3xl p-8 flex flex-col items-center justify-center text-brand-green bg-brand-mint/10 hover:bg-brand-mint/20 cursor-pointer transition-colors">
            <UploadSimple size={28} weight="duotone" />
            <span className="text-sm mt-2">{file ? file.name : 'Selecionar ficheiro CSV ou XLSX'}</span>
            <span className="text-[10px] text-brand-muted mt-1">Campos: nome, email, telefone, data_nascimento, tipo, programa, observacoes, status</span>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="patient-import-file" />
          </label>
        </div>

        <div className="border-t border-brand-border pt-6">
          <button onClick={handleImport} disabled={!file || importing} className="w-full bg-brand-gold text-white hover:bg-brand-gold-hover transition-all rounded-full px-6 py-3.5 font-medium disabled:opacity-50" data-testid="submit-patient-import">
            {importing ? 'A importar...' : 'Importar pacientes'}
          </button>
        </div>

        {results && (
          <div className="border-t border-brand-border pt-6" data-testid="import-results">
            <h3 className="font-heading font-medium text-sm text-brand-text mb-3">Resultados</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-brand-mint/10 rounded-2xl p-3 text-center">
                <p className="text-2xl font-heading font-medium text-brand-green">{results.imported}</p>
                <p className="text-xs text-brand-muted">Importados</p>
              </div>
              <div className="bg-brand-gold/10 rounded-2xl p-3 text-center">
                <p className="text-2xl font-heading font-medium text-brand-gold-hover">{results.updated}</p>
                <p className="text-xs text-brand-muted">Atualizados</p>
              </div>
            </div>
            {results.by_status && (
              <div className="grid grid-cols-3 gap-2 text-center" data-testid="status-breakdown">
                <div className="bg-brand-bg rounded-xl p-2"><p className="text-base font-medium text-brand-text">{results.by_status.ativo || 0}</p><p className="text-[10px] text-brand-muted">Ativos</p></div>
                <div className="bg-brand-bg rounded-xl p-2"><p className="text-base font-medium text-brand-muted">{results.by_status.arquivado || 0}</p><p className="text-[10px] text-brand-muted">Arquivados</p></div>
                <div className="bg-brand-bg rounded-xl p-2"><p className="text-base font-medium text-brand-muted">{results.by_status.inativo || 0}</p><p className="text-[10px] text-brand-muted">Inativos</p></div>
              </div>
            )}
            {results.errors?.length > 0 && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-2xl p-3">
                <p className="text-xs font-medium text-red-600 mb-1">{results.errors.length} erros:</p>
                {results.errors.slice(0, 5).map((e, i) => <p key={i} className="text-xs text-red-500">{e}</p>)}
                {results.errors.length > 5 && <p className="text-xs text-red-500 italic">(+{results.errors.length - 5} mais)</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
